import { ApiError, NETWORK_ERROR_STATUS, api } from './api';
import { normalizeIso } from './time';
import type { TaskStatus } from './types';

/**
 * Fronta na zmeny urobené bez siete.
 *
 * Zámerne úzka: komentár k tiketu, správa v chate, poznámka, osobná úloha a
 * presun tiketu do iného stĺpca. To sú veci, ktoré človek robí vo výťahu alebo
 * vo vlaku a ktoré nič iné nepotrebujú. Súbory, ankety, mazanie a úpravy
 * cudzích vecí sem nepatria — tie bez servera nemajú zmysel alebo sa nedajú
 * bezpečne zopakovať.
 *
 * Pravidlá, na ktorých to celé stojí:
 *
 * - **Poradie.** Posiela sa v poradí, v akom sa zmeny urobili. Kým vo fronte
 *   niečo čaká, aj nová zmena ide do fronty — inak by predbehla staršiu.
 * - **Nikdy dvakrát.** Žiadna z týchto rout nevie odlíšiť opakovanie od novej
 *   požiadavky a bez migrácie sa to naučiť nedá. Preto: jedno odosielanie naraz
 *   (aj naprieč kartami cez Web Locks), položka sa označí ako „rozoslaná" ešte
 *   PRED odoslaním a z fronty zmizne hneď po potvrdení, pred ďalšou. Keď spojenie
 *   padne uprostred (server mohol zapísať, len odpoveď neprišla), pri ďalšom
 *   pokuse sa najprv pozrie, či to na serveri už nie je.
 * - **Nič potichu.** Keď server zmenu odmietne (nemáš prístup, ticket zmizol),
 *   položka ostane vo fronte ako neúspešná aj s dôvodom, kým ju človek nezahodí
 *   alebo neskúsi znova. Automaticky sa neopakuje.
 * - **CSRF z chvíle odoslania.** Posiela sa cez `api`, ktoré token číta až pri
 *   volaní — nie ten, ktorý platil, keď sa zmena zaradila.
 */

export type ChatChannel =
  | { kind: 'general' }
  | { kind: 'project'; projectId: number }
  | { kind: 'dm'; userId: number };

export type QueuedAction =
  | { kind: 'taskComment'; taskId: number; body: string }
  | { kind: 'chatMessage'; channel: ChatChannel; body: string; replyToId?: number }
  | {
      kind: 'note';
      payload: {
        userId?: number;
        title: string;
        content?: string;
        color?: string;
        pinned?: boolean;
        visibility?: 'private' | 'public';
        projectId?: number | null;
      };
    }
  | {
      kind: 'todo';
      payload: {
        projectId?: number | null;
        userId?: number;
        title: string;
        note?: string;
        priority?: 'low' | 'normal' | 'high' | 'urgent';
        dueAt?: string | null;
      };
    }
  | { kind: 'taskStatus'; taskId: number; status: TaskStatus };

export type QueueItem = {
  /** Vygenerované v prehliadači; slúži na odlíšenie položiek, nie serveru. */
  id: string;
  action: QueuedAction;
  /** Kto zmenu urobil. Pod iným prihlásením sa neposiela. */
  userId: number;
  queuedAt: string;
  attempts: number;
  /** Odosielanie sa začalo a nevieme, či dobehlo. */
  uncertain: boolean;
  /** Server zmenu odmietol. Čaká na rozhodnutie človeka. */
  failed: { status: number; message: string } | null;
};

/** Po toľkých pokusoch bez odpovede sa položka prestane skúšať sama. */
export const MAX_ATTEMPTS = 8;

/** Rozdiel hodín medzi telefónom a serverom, ktorý ešte tolerujeme. */
const CLOCK_SKEW_MS = 10 * 60_000;

export const QUEUE_STORAGE_KEY = 'whoworks:offline-queue:v1';

export type QueueStorage = {
  read: () => QueueItem[];
  write: (items: QueueItem[]) => void;
};

/**
 * localStorage, ale nikdy nie ako dôvod na pád. Súkromné okno, plné úložisko
 * alebo zakázané dáta stránky — fronta vtedy žije aspoň v pamäti, kým je
 * karta otvorená.
 */
export const browserStorage = (key = QUEUE_STORAGE_KEY): QueueStorage => {
  let memory: QueueItem[] = [];
  return {
    read: () => {
      try {
        const raw = window.localStorage.getItem(key);
        if (raw === null) {
          return memory;
        }
        const parsed: unknown = JSON.parse(raw);
        return Array.isArray(parsed) ? (parsed as QueueItem[]) : [];
      } catch {
        return memory;
      }
    },
    write: (items) => {
      memory = items;
      try {
        if (items.length === 0) {
          window.localStorage.removeItem(key);
        } else {
          window.localStorage.setItem(key, JSON.stringify(items));
        }
      } catch {
        // Ostáva kópia v pamäti.
      }
    },
  };
};

export type Transport = {
  send: (action: QueuedAction) => Promise<unknown>;
  /** Je zmena už na serveri? Pýta sa iba po nejasnom pokuse. */
  alreadyApplied: (item: QueueItem) => Promise<boolean>;
};

export type QueueEnv = {
  storage: QueueStorage;
  transport: Transport;
  /** Zámok naprieč kartami. Bez neho sa použije zámok v rámci karty. */
  lock?: <T>(work: () => Promise<T>) => Promise<T>;
  isOnline?: () => boolean;
  now?: () => Date;
  newId?: () => string;
};

export type FlushResult = { sent: QueueItem[]; failed: QueueItem[]; stopped: boolean };

const isNetwork = (error: unknown): boolean =>
  error instanceof ApiError && error.status === NETWORK_ERROR_STATUS;

const defaultId = (): string => {
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }
  } catch {
    // Nižšie je náhrada.
  }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
};

export const createOfflineQueue = (env: QueueEnv) => {
  const now = env.now ?? (() => new Date());
  const newId = env.newId ?? defaultId;
  const isOnline = env.isOnline ?? (() => typeof navigator === 'undefined' || navigator.onLine !== false);
  const listeners = new Set<() => void>();
  let version = 0;
  let currentUserId: number | null = null;
  let inFlight: Promise<FlushResult> | null = null;

  const notify = () => {
    version += 1;
    for (const listener of listeners) {
      listener();
    }
  };

  const read = () => env.storage.read();
  const write = (items: QueueItem[]) => {
    env.storage.write(items);
    notify();
  };
  const update = (id: string, change: (item: QueueItem) => QueueItem | null) => {
    const next: QueueItem[] = [];
    for (const item of read()) {
      if (item.id !== id) {
        next.push(item);
        continue;
      }
      const changed = change(item);
      if (changed) {
        next.push(changed);
      }
    }
    write(next);
  };

  const mine = (items: QueueItem[]) =>
    currentUserId === null ? [] : items.filter((item) => item.userId === currentUserId);

  const enqueue = (action: QueuedAction, options: { uncertain?: boolean } = {}): QueueItem => {
    if (currentUserId === null) {
      throw new Error('Fronta nevie, kto je prihlásený.');
    }
    const item: QueueItem = {
      id: newId(),
      action,
      userId: currentUserId,
      queuedAt: now().toISOString(),
      attempts: options.uncertain ? 1 : 0,
      uncertain: Boolean(options.uncertain),
      failed: null,
    };
    write([...read(), item]);
    return item;
  };

  const runFlush = async (): Promise<FlushResult> => {
    const result: FlushResult = { sent: [], failed: [], stopped: false };
    // Zoznam sa číta znova pred každou položkou: iná karta (alebo „Zahodiť")
    // ho mohla medzitým zmeniť a odoslať sa smie iba to, čo tam ešte je.
    for (;;) {
      if (!isOnline()) {
        result.stopped = true;
        return result;
      }
      const next = mine(read()).find((item) => item.failed === null);
      if (!next) {
        return result;
      }

      if (next.uncertain) {
        let applied: boolean;
        try {
          applied = await env.transport.alreadyApplied(next);
        } catch (error) {
          if (isNetwork(error)) {
            result.stopped = true;
            return result;
          }
          // Keď sa nedá overiť, radšej to nechať na človeka než riskovať dvojičku.
          const failed = {
            status: error instanceof ApiError ? error.status : 0,
            message: 'Nedá sa overiť, či sa to už neodoslalo.',
          };
          // `uncertain` ostáva: aj po „Skúsiť znova" sa najprv overuje.
          update(next.id, (item) => ({ ...item, failed }));
          result.failed.push({ ...next, failed });
          continue;
        }
        if (applied) {
          update(next.id, () => null);
          result.sent.push(next);
          continue;
        }
      }

      if (next.attempts >= MAX_ATTEMPTS) {
        const failed = {
          status: NETWORK_ERROR_STATUS,
          message: `Nepodarilo sa odoslať ani na ${MAX_ATTEMPTS}. pokus.`,
        };
        update(next.id, (item) => ({ ...item, failed }));
        result.failed.push({ ...next, failed });
        continue;
      }

      // Najprv zapísať, že sa posiela. Keby sa karta zavrela uprostred, ďalší
      // pokus to bude vedieť a pred odoslaním sa pozrie na server.
      update(next.id, (item) => ({ ...item, uncertain: true, attempts: item.attempts + 1 }));
      try {
        await env.transport.send(next.action);
      } catch (error) {
        if (isNetwork(error)) {
          result.stopped = true;
          return result;
        }
        if (error instanceof ApiError && error.status === 401) {
          // Odhlásený. Zmena nie je zlá, len ju teraz nemá kto poslať.
          update(next.id, (item) => ({ ...item, uncertain: false }));
          result.stopped = true;
          return result;
        }
        const failed = {
          status: error instanceof ApiError ? error.status : -1,
          message: (error as Error)?.message || 'Server zmenu odmietol.',
        };
        update(next.id, (item) => ({ ...item, uncertain: false, failed }));
        result.failed.push({ ...next, failed });
        continue;
      }
      // Preč z fronty skôr, než sa začne ďalšia — to je celá ochrana pred tým,
      // aby sa potvrdená zmena poslala druhýkrát.
      update(next.id, () => null);
      result.sent.push(next);
    }
  };

  /**
   * Odošle, čo čaká. Súbežné volania dostanú ten istý beh — nikdy nebežia dva
   * naraz v jednej karte, a so zámkom ani v dvoch kartách.
   */
  const flush = (): Promise<FlushResult> => {
    if (inFlight) {
      return inFlight;
    }
    const run = env.lock ? env.lock(runFlush) : runFlush();
    inFlight = run.finally(() => {
      inFlight = null;
    });
    return inFlight;
  };

  return {
    setUser: (userId: number | null) => {
      currentUserId = userId;
      notify();
    },
    enqueue,
    flush,
    /** Mení sa pri každej zmene fronty — pre useSyncExternalStore. */
    version: () => version,
    /** Úložisko zmenila iná karta; nech sa ukazovateľ prepočíta. */
    refresh: notify,
    items: (): QueueItem[] => mine(read()),
    pendingCount: (): number => mine(read()).filter((item) => item.failed === null).length,
    retry: (id: string) => {
      update(id, (item) => ({ ...item, failed: null, attempts: 0 }));
      return flush();
    },
    discard: (id: string) => update(id, () => null),
    subscribe: (listener: () => void) => {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    /**
     * Pošle hneď, alebo zaradí. Zaradí, keď niet siete, keď vo fronte už niečo
     * čaká (poradie), alebo keď spojenie padne uprostred — vtedy s príznakom
     * „nejasné", aby sa pred ďalším pokusom overilo, či to server už nemá.
     *
     * Chyba od servera (nie od siete) sa vyhodí ako doteraz: formulár ju ukáže
     * a človek je pri tom, takže fronta tu nemá čo robiť.
     */
    sendOrQueue: async <T>(
      action: QueuedAction,
      send: () => Promise<T>,
    ): Promise<{ queued: true; item: QueueItem } | { queued: false; result: T }> => {
      if (currentUserId === null) {
        return { queued: false, result: await send() };
      }
      if (!isOnline() || mine(read()).some((item) => item.failed === null)) {
        const item = enqueue(action);
        if (isOnline()) {
          void flush();
        }
        return { queued: true, item };
      }
      try {
        return { queued: false, result: await send() };
      } catch (error) {
        if (!isNetwork(error)) {
          throw error;
        }
        return { queued: true, item: enqueue(action, { uncertain: true }) };
      }
    },
  };
};

export type OfflineQueue = ReturnType<typeof createOfflineQueue>;

// --- Prepojenie na skutočné API --------------------------------------------------

const sameText = (a: string | null | undefined, b: string | null | undefined): boolean =>
  (a ?? '').trim() === (b ?? '').trim();

/** Vznikla vec na serveri až po tom, čo sa zmena zaradila (s rezervou na hodiny)? */
const createdAfter = (createdAt: string, queuedAt: string): boolean =>
  new Date(normalizeIso(createdAt)).getTime() >= new Date(queuedAt).getTime() - CLOCK_SKEW_MS;

export const sendAction = (action: QueuedAction): Promise<unknown> => {
  switch (action.kind) {
    case 'taskComment':
      return api.taskCommentCreate(action.taskId, { body: action.body });
    case 'chatMessage':
      return action.channel.kind === 'general'
        ? api.generalMessagesCreate(action.body, action.replyToId)
        : action.channel.kind === 'dm'
          ? api.dmMessagesCreate(action.channel.userId, action.body, action.replyToId)
          : api.messagesCreate(action.channel.projectId, action.body, action.replyToId);
    case 'note':
      return api.notesCreate(action.payload);
    case 'todo':
      return api.tasksCreate(action.payload);
    case 'taskStatus':
      return api.tasksUpdate(action.taskId, { status: action.status });
  }
};

/**
 * Pozrie, či nejasne odoslaná zmena na serveri už je. Porovnáva autora, text
 * a čas vzniku — rovnaký text od toho istého človeka po zaradení zmeny je tá
 * istá zmena, nie náhoda.
 */
export const alreadyApplied = async (item: QueueItem): Promise<boolean> => {
  const { action } = item;
  switch (action.kind) {
    case 'taskComment': {
      const { items } = await api.taskCommentsList(action.taskId);
      return items.some(
        (c) => c.userId === item.userId && sameText(c.body, action.body) && createdAfter(c.createdAt, item.queuedAt),
      );
    }
    case 'chatMessage': {
      const list =
        action.channel.kind === 'general'
          ? await api.generalMessagesList({ limit: 50 })
          : action.channel.kind === 'dm'
            ? await api.dmMessagesList(action.channel.userId, { limit: 50 })
            : await api.messagesList(action.channel.projectId, { limit: 50 });
      return list.items.some(
        (m) => m.userId === item.userId && sameText(m.body, action.body) && createdAfter(m.createdAt, item.queuedAt),
      );
    }
    case 'note': {
      const { items } = await api.notesList({ includeArchived: 1 });
      return items.some(
        (n) =>
          n.userId === item.userId &&
          sameText(n.title, action.payload.title) &&
          createdAfter(n.createdAt, item.queuedAt),
      );
    }
    case 'todo': {
      const { items } = await api.tasksList({
        projectId: action.payload.projectId ?? undefined,
        userId: action.payload.userId,
        includeCompleted: 1,
      });
      return items.some(
        (t) =>
          (t.createdByUserId ?? t.userId) === item.userId &&
          sameText(t.title, action.payload.title) &&
          createdAfter(t.createdAt, item.queuedAt),
      );
    }
    case 'taskStatus':
      // Nastaviť stav na tú istú hodnotu druhýkrát nič nepokazí — netreba
      // sa pýtať, stačí poslať znova.
      return false;
  }
};

const webLock = <T>(work: () => Promise<T>): Promise<T> => {
  const locks = typeof navigator !== 'undefined' ? navigator.locks : undefined;
  if (!locks || typeof locks.request !== 'function') {
    return work();
  }
  return locks.request('whoworks-offline-queue', work) as Promise<T>;
};

let singleton: OfflineQueue | null = null;

/** Jedna fronta pre celú appku. */
export const offlineQueue = (): OfflineQueue => {
  singleton ??= createOfflineQueue({
    storage: browserStorage(),
    transport: { send: sendAction, alreadyApplied },
    lock: webLock,
  });
  return singleton;
};

/** Text upozornenia po zaradení. Jeden, aby všade znel rovnako. */
export const queuedMessage = (): string =>
  typeof navigator !== 'undefined' && navigator.onLine === false
    ? 'Bez pripojenia — odošle sa, keď bude sieť.'
    : 'Čaká vo fronte, odošle sa po predošlých zmenách.';
